/**
 * Unread DM count for the Connect pillar's bottom-nav badge and its
 * Messages sub-tab (2026-08-15, notification-prominence follow-up).
 *
 * Polling backoff reuses `computeNextHelmUnreadPollInterval` from
 * `useLayout.ts` rather than reimplementing it — the function is a pure
 * {status, data} -> interval step with no Helm-specific coupling (30s ->
 * 60s -> ... -> 5min cap on consecutive zero results, reset to 30s on any
 * nonzero result, unchanged on error).
 */

import { useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../../contexts/AuthContext';
import { messagesAPI } from '../../services/api/messages';
import { unwrapResponse } from '../../utils/unwrapResponse';
import { queryKeys } from './queryKeys';
import { computeNextHelmUnreadPollInterval } from '../../components/layout/useLayout';

const INITIAL_INTERVAL = 30000; // 30 seconds, matches useLayout.ts's helm-unread poll

export function useUnreadMessageCount() {
  const { user } = useAuth();
  const currentIntervalRef = useRef(INITIAL_INTERVAL);

  const query = useQuery({
    queryKey: queryKeys.messages.unreadCount(user?.id || ''),
    queryFn: async ({ signal }) => {
      const response = await messagesAPI.getUnreadCount(signal);
      return unwrapResponse<{ unreadCount: number }>(response.data);
    },
    enabled: !!user,
    retry: false,
    refetchInterval: (q) => {
      currentIntervalRef.current = computeNextHelmUnreadPollInterval(currentIntervalRef.current, {
        status: q.state.status,
        data: q.state.data?.unreadCount,
      });
      return currentIntervalRef.current;
    },
  });

  return query.data?.unreadCount ?? 0;
}
